import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { BookingForm, PressKitFieldsPatch, Talent } from "@shared/schema";

type ListField = "videoLinks" | "audioLinks" | "galleryImageUrls" | "logoUrls";

interface CreditRow { title: string; year?: string; role?: string; description?: string }
interface QuoteRow { quote: string; source?: string; sourceUrl?: string }
interface FaqRow { question: string; answer: string }

interface PressKitFormState {
  pressKitPublished: boolean;
  heroPhotoUrl: string;
  longBio: string;
  videoLinks: string[];
  audioLinks: string[];
  galleryImageUrls: string[];
  logoUrls: string[];
  credits: CreditRow[];
  pressQuotes: QuoteRow[];
  faqItems: FaqRow[];
  epkUrl: string;
  techRiderUrl: string;
  hospitalityRiderUrl: string;
  stagePlotUrl: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactCompany: string;
  pressKitDefaultBookingFormId: string;
}

function fromTalent(t: Talent): PressKitFormState {
  return {
    pressKitPublished: !!t.pressKitPublished,
    heroPhotoUrl: t.heroPhotoUrl ?? "",
    longBio: t.longBio ?? "",
    videoLinks: t.videoLinks ?? [],
    audioLinks: t.audioLinks ?? [],
    galleryImageUrls: t.galleryImageUrls ?? [],
    logoUrls: t.logoUrls ?? [],
    credits: (t.credits as CreditRow[] | null) ?? [],
    pressQuotes: (t.pressQuotes as QuoteRow[] | null) ?? [],
    faqItems: (t.faqItems as FaqRow[] | null) ?? [],
    epkUrl: t.epkUrl ?? "",
    techRiderUrl: t.techRiderUrl ?? "",
    hospitalityRiderUrl: t.hospitalityRiderUrl ?? "",
    stagePlotUrl: t.stagePlotUrl ?? "",
    contactName: t.contactName ?? "",
    contactEmail: t.contactEmail ?? "",
    contactPhone: t.contactPhone ?? "",
    contactCompany: t.contactCompany ?? "",
    pressKitDefaultBookingFormId: t.pressKitDefaultBookingFormId ?? "",
  };
}

function toPatch(s: PressKitFormState): PressKitFieldsPatch {
  // null-out empty optional URL/text fields so the server clears them
  // (instead of saving empty strings that the URL validator rejects).
  const optionalString = (v: string) => (v.trim() === "" ? null : v.trim());
  return {
    pressKitPublished: s.pressKitPublished,
    heroPhotoUrl: optionalString(s.heroPhotoUrl),
    longBio: optionalString(s.longBio),
    videoLinks: s.videoLinks.filter(Boolean),
    audioLinks: s.audioLinks.filter(Boolean),
    galleryImageUrls: s.galleryImageUrls.filter(Boolean),
    logoUrls: s.logoUrls.filter(Boolean),
    credits: s.credits.filter((c) => c.title?.trim()),
    pressQuotes: s.pressQuotes.filter((q) => q.quote?.trim()),
    faqItems: s.faqItems.filter((f) => f.question?.trim()),
    epkUrl: optionalString(s.epkUrl),
    techRiderUrl: optionalString(s.techRiderUrl),
    hospitalityRiderUrl: optionalString(s.hospitalityRiderUrl),
    stagePlotUrl: optionalString(s.stagePlotUrl),
    contactName: optionalString(s.contactName),
    contactEmail: optionalString(s.contactEmail),
    contactPhone: optionalString(s.contactPhone),
    contactCompany: optionalString(s.contactCompany),
    pressKitDefaultBookingFormId: optionalString(s.pressKitDefaultBookingFormId),
  };
}

function StringList({
  label,
  values,
  placeholder,
  onChange,
  testId,
}: {
  label: string;
  values: string[];
  placeholder: string;
  onChange: (next: string[]) => void;
  testId: string;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {values.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input
            value={v}
            placeholder={placeholder}
            onChange={(e) => {
              const next = values.slice();
              next[i] = e.target.value;
              onChange(next);
            }}
            data-testid={`${testId}-${i}`}
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onChange(values.filter((_, idx) => idx !== i))}
            data-testid={`${testId}-remove-${i}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...values, ""])} data-testid={`${testId}-add`}>
        <Plus className="h-3 w-3 mr-1" /> Add
      </Button>
    </div>
  );
}

export interface PressKitEditorProps {
  talent: Talent;
  // When true the editor surfaces a "Submit for approval" hint + uses the
  // press-kit endpoint that may queue the change. The backend always
  // resolves routing — this prop only changes the button copy.
  awaitingApproval?: boolean;
}

export function PressKitEditor({ talent, awaitingApproval }: PressKitEditorProps) {
  const { toast } = useToast();
  const [state, setState] = useState<PressKitFormState>(() => fromTalent(talent));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setState(fromTalent(talent));
    setDirty(false);
  }, [talent]);

  const { data: bookingForms = [] } = useQuery<BookingForm[]>({
    queryKey: [`/api/talent/${talent.id}/booking-forms`],
  });

  const save = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", `/api/talent/${talent.id}/press-kit`, toPatch(state));
      return (await res.json()) as { status: "saved" | "queued" };
    },
    onSuccess: (data) => {
      toast({
        title: data.status === "queued" ? "Submitted for approval" : "Saved",
        description: data.status === "queued" ? "Your edit is waiting for an approver." : "Your press kit is updated.",
      });
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ["/api/talent"] });
      queryClient.invalidateQueries({ queryKey: [`/api/talent/${talent.id}/pending-edits`] });
    },
    onError: (e: Error) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const setField = <K extends keyof PressKitFormState>(k: K, v: PressKitFormState[K]) => {
    setState((s) => ({ ...s, [k]: v }));
    setDirty(true);
  };
  const setList = (k: ListField, v: string[]) => setField(k, v);

  const publicUrl = talent.bookingSlug ? `${window.location.origin}/artist/${talent.bookingSlug}` : null;

  return (
    <div className="space-y-6" data-testid="press-kit-editor">
      <Card>
        <CardHeader><CardTitle className="text-base">Public press-kit page</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Published</Label>
              <p className="text-xs text-muted-foreground">When off, the public page returns 404.</p>
            </div>
            <Switch
              checked={state.pressKitPublished}
              onCheckedChange={(v) => setField("pressKitPublished", v)}
              data-testid="switch-press-kit-published"
            />
          </div>
          {publicUrl && (
            <div className="flex items-center gap-2 text-sm">
              <Input value={publicUrl} readOnly className="font-mono text-xs" data-testid="input-press-kit-url" />
              <a href={publicUrl} target="_blank" rel="noreferrer">
                <Button variant="outline" size="icon"><ExternalLink className="h-4 w-4" /></Button>
              </a>
            </div>
          )}
          <div className="space-y-2">
            <Label>Hero photo URL</Label>
            <Input value={state.heroPhotoUrl} onChange={(e) => setField("heroPhotoUrl", e.target.value)} placeholder="https://…" data-testid="input-hero-photo" />
          </div>
          <div className="space-y-2">
            <Label>Long bio</Label>
            <Textarea
              rows={6}
              value={state.longBio}
              onChange={(e) => setField("longBio", e.target.value)}
              data-testid="input-long-bio"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Media</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <StringList label="Videos (YouTube/Vimeo URLs)" values={state.videoLinks} placeholder="https://youtube.com/watch?v=…" onChange={(v) => setList("videoLinks", v)} testId="video-link" />
          <StringList label="Audio (Spotify/SoundCloud URLs)" values={state.audioLinks} placeholder="https://open.spotify.com/…" onChange={(v) => setList("audioLinks", v)} testId="audio-link" />
          <StringList label="Gallery image URLs" values={state.galleryImageUrls} placeholder="https://…" onChange={(v) => setList("galleryImageUrls", v)} testId="gallery-link" />
          <StringList label="Brand / venue logo URLs" values={state.logoUrls} placeholder="https://…" onChange={(v) => setList("logoUrls", v)} testId="logo-link" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Credits & press</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Credits</Label>
            {state.credits.map((c, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-2 border rounded p-2">
                <Input placeholder="Title" value={c.title} onChange={(e) => { const n = state.credits.slice(); n[i] = { ...n[i]!, title: e.target.value }; setField("credits", n); }} data-testid={`credit-title-${i}`} />
                <Input placeholder="Year" value={c.year ?? ""} onChange={(e) => { const n = state.credits.slice(); n[i] = { ...n[i]!, year: e.target.value }; setField("credits", n); }} data-testid={`credit-year-${i}`} />
                <Input placeholder="Role" value={c.role ?? ""} onChange={(e) => { const n = state.credits.slice(); n[i] = { ...n[i]!, role: e.target.value }; setField("credits", n); }} data-testid={`credit-role-${i}`} />
                <div className="flex gap-2">
                  <Input placeholder="Description" value={c.description ?? ""} onChange={(e) => { const n = state.credits.slice(); n[i] = { ...n[i]!, description: e.target.value }; setField("credits", n); }} data-testid={`credit-desc-${i}`} />
                  <Button variant="ghost" size="icon" onClick={() => setField("credits", state.credits.filter((_, idx) => idx !== i))} data-testid={`credit-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setField("credits", [...state.credits, { title: "" }])} data-testid="credit-add"><Plus className="h-3 w-3 mr-1" /> Add credit</Button>
          </div>

          <div className="space-y-2">
            <Label>Press quotes</Label>
            {state.pressQuotes.map((q, i) => (
              <div key={i} className="space-y-2 border rounded p-2">
                <Textarea placeholder="Quote" value={q.quote} rows={2} onChange={(e) => { const n = state.pressQuotes.slice(); n[i] = { ...n[i]!, quote: e.target.value }; setField("pressQuotes", n); }} data-testid={`quote-text-${i}`} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input placeholder="Source (e.g. Rolling Stone)" value={q.source ?? ""} onChange={(e) => { const n = state.pressQuotes.slice(); n[i] = { ...n[i]!, source: e.target.value }; setField("pressQuotes", n); }} data-testid={`quote-source-${i}`} />
                  <div className="flex gap-2">
                    <Input placeholder="Source URL" value={q.sourceUrl ?? ""} onChange={(e) => { const n = state.pressQuotes.slice(); n[i] = { ...n[i]!, sourceUrl: e.target.value }; setField("pressQuotes", n); }} data-testid={`quote-url-${i}`} />
                    <Button variant="ghost" size="icon" onClick={() => setField("pressQuotes", state.pressQuotes.filter((_, idx) => idx !== i))} data-testid={`quote-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setField("pressQuotes", [...state.pressQuotes, { quote: "" }])} data-testid="quote-add"><Plus className="h-3 w-3 mr-1" /> Add quote</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Documents</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1"><Label>EPK URL</Label><Input value={state.epkUrl} onChange={(e) => setField("epkUrl", e.target.value)} placeholder="https://…" data-testid="input-epk-url" /></div>
          <div className="space-y-1"><Label>Tech rider URL</Label><Input value={state.techRiderUrl} onChange={(e) => setField("techRiderUrl", e.target.value)} placeholder="https://…" data-testid="input-tech-rider" /></div>
          <div className="space-y-1"><Label>Hospitality rider URL</Label><Input value={state.hospitalityRiderUrl} onChange={(e) => setField("hospitalityRiderUrl", e.target.value)} placeholder="https://…" data-testid="input-hospitality-rider" /></div>
          <div className="space-y-1"><Label>Stage plot URL</Label><Input value={state.stagePlotUrl} onChange={(e) => setField("stagePlotUrl", e.target.value)} placeholder="https://…" data-testid="input-stage-plot" /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">FAQ</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {state.faqItems.map((f, i) => (
            <div key={i} className="space-y-2 border rounded p-2">
              <Input placeholder="Question" value={f.question} onChange={(e) => { const n = state.faqItems.slice(); n[i] = { ...n[i]!, question: e.target.value }; setField("faqItems", n); }} data-testid={`faq-q-${i}`} />
              <div className="flex gap-2">
                <Textarea placeholder="Answer" rows={2} value={f.answer} onChange={(e) => { const n = state.faqItems.slice(); n[i] = { ...n[i]!, answer: e.target.value }; setField("faqItems", n); }} data-testid={`faq-a-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => setField("faqItems", state.faqItems.filter((_, idx) => idx !== i))} data-testid={`faq-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setField("faqItems", [...state.faqItems, { question: "", answer: "" }])} data-testid="faq-add"><Plus className="h-3 w-3 mr-1" /> Add FAQ</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Contact card & booking CTA</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1"><Label>Contact name</Label><Input value={state.contactName} onChange={(e) => setField("contactName", e.target.value)} data-testid="input-contact-name" /></div>
          <div className="space-y-1"><Label>Company</Label><Input value={state.contactCompany} onChange={(e) => setField("contactCompany", e.target.value)} data-testid="input-contact-company" /></div>
          <div className="space-y-1"><Label>Email</Label><Input type="email" value={state.contactEmail} onChange={(e) => setField("contactEmail", e.target.value)} data-testid="input-contact-email" /></div>
          <div className="space-y-1"><Label>Phone</Label><Input value={state.contactPhone} onChange={(e) => setField("contactPhone", e.target.value)} data-testid="input-contact-phone" /></div>
          <div className="space-y-1 md:col-span-2">
            <Label>Default booking form (CTA target)</Label>
            <select
              className="border rounded px-2 py-1.5 w-full bg-background"
              value={state.pressKitDefaultBookingFormId}
              onChange={(e) => setField("pressKitDefaultBookingFormId", e.target.value)}
              data-testid="select-default-booking-form"
            >
              <option value="">— Use workspace default —</option>
              {bookingForms.map((f) => (
                <option key={f.id} value={f.id}>{f.name}{f.isDefault ? " (default)" : ""}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        {awaitingApproval ? (
          <Badge variant="outline">Edits queue for approval</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">Edits save immediately</span>
        )}
        <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending} data-testid="button-save-press-kit">
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : awaitingApproval ? "Submit for approval" : "Save press kit"}
        </Button>
      </div>
    </div>
  );
}
